import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { criminalEvents } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useNestedList } from "@/hooks/useNestedList";
import {
  combineFieldErrors,
  combineFormError,
  getFieldErrors,
  getFormError,
  runUpserts,
} from "@/lib/validation-errors";
import { Field, Select, TextInput, YesNo } from "@/components/Field";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import type {
  CreateCriminalEventRequest,
  CriminalEventResponse,
} from "@/lib/api-types";
import { useFormResponses } from "@/hooks/useFormResponses";

/**
 * Step 7 — Background (Part 9 of N-400).
 *
 * Two parts:
 *   1) Criminal-history toggle + per-event editor (gates MVP eligibility:
 *      "yes" routes to v2 waitlist via the gating engine).
 *   2) Other Part 9 yes/no + free-text questions the form mapping needs:
 *      SSA authorisation, selective service, preferred USCIS office,
 *      hereditary titles.
 */
const FR_KEY = "has_criminal_history";

// form_responses keys for the additional Part 9 background questions
// the mapper reads. Values must match the strings in n400_2025_01_20.go.
const FR_KEYS = {
  authorizeSSA: "authorize_ssa_disclosure",
  updateSSA: "update_ssa_record",
  selectiveServiceDate: "selective_service_date",
  selectiveServiceNumber: "selective_service_number",
  uscisFieldOffice: "uscis_field_office",
  nobilityTitles: "nobility_titles",
  // Supplemental "have you ever..." Y/N questions the rules engine
  // requires before the case is Ready. Almost all applicants answer
  // these "No" — but the answer must be explicit, not assumed.
  registeredSelectiveService: "registered_selective_service",
  everCommunist: "ever_been_member_communist_party",
  everTerrorist: "ever_been_terrorist",
  everPersecuted: "ever_persecuted_any_person",
  everCommittedCrime: "ever_committed_crime_not_arrested",
  everFailedTaxes: "ever_failed_to_file_taxes",
  everOwedTaxes: "ever_owed_taxes",
} as const;

// "Have you ever…" questions, evaluator-required Y/N flags. Wired as
// a flat list because the wording is what differs, not the shape.
const EVER_QUESTIONS: { key: keyof typeof FR_KEYS; label: string; helper?: string }[] = [
  {
    key: "registeredSelectiveService",
    label: "Have you registered for Selective Service?",
    helper: "Required only for males born after 12/31/1959 who lived in the U.S. between ages 18 and 26.",
  },
  {
    key: "everCommunist",
    label: "Have you ever been a member of the Communist Party (or any totalitarian party) anywhere in the world?",
  },
  {
    key: "everTerrorist",
    label: "Have you ever been associated with a terrorist organization?",
  },
  {
    key: "everPersecuted",
    label: "Have you ever persecuted any person based on race, religion, national origin, social group, or political opinion?",
  },
  {
    key: "everCommittedCrime",
    label: "Have you ever committed a crime for which you were not arrested?",
  },
  {
    key: "everFailedTaxes",
    label: "Have you ever failed to file a required federal, state, or local tax return?",
  },
  {
    key: "everOwedTaxes",
    label: "Do you owe any overdue federal, state, or local taxes?",
  },
];

export function StepCriminal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const fr = useFormResponses(id);
  const list = useNestedList<CriminalEventResponse, CreateCriminalEventRequest>(
    "criminal-events",
    criminalEvents,
    id,
    applicant?.id,
  );

  const [editing, setEditing] = useState<CriminalEventResponse | "new" | null>(
    null,
  );

  const flag = fr.get<boolean>(FR_KEY);
  // Treat undefined as "user hasn't answered yet"; show the toggle.
  const hasHistory = typeof flag === "boolean" ? flag : null;

  // Additional Part 9 questions — each driven by its own form_response.
  const [authorizeSSA, setAuthorizeSSA] = useState<boolean | null>(null);
  const [updateSSA, setUpdateSSA] = useState<boolean | null>(null);
  const [selectiveServiceDate, setSelectiveServiceDate] = useState("");
  const [selectiveServiceNumber, setSelectiveServiceNumber] = useState("");
  const [uscisFieldOffice, setUscisFieldOffice] = useState("");
  const [nobilityTitles, setNobilityTitles] = useState("");
  // Evaluator-required Y/N flags ("have you ever…"). Tri-state because
  // unanswered ≠ "No" — rules engine wants an explicit answer.
  const [everAnswers, setEverAnswers] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    if (fr.isLoading) return;
    const a = fr.get<boolean>(FR_KEYS.authorizeSSA);
    setAuthorizeSSA(typeof a === "boolean" ? a : null);
    const u = fr.get<boolean>(FR_KEYS.updateSSA);
    setUpdateSSA(typeof u === "boolean" ? u : null);
    setSelectiveServiceDate(fr.getString(FR_KEYS.selectiveServiceDate));
    setSelectiveServiceNumber(fr.getString(FR_KEYS.selectiveServiceNumber));
    setUscisFieldOffice(fr.getString(FR_KEYS.uscisFieldOffice));
    setNobilityTitles(fr.getString(FR_KEYS.nobilityTitles));
    const next: Record<string, boolean | null> = {};
    for (const q of EVER_QUESTIONS) {
      const v = fr.get<boolean>(FR_KEYS[q.key]);
      next[q.key] = typeof v === "boolean" ? v : null;
    }
    setEverAnswers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);
  const frFieldErrors = combineFieldErrors(saveErrors);
  const frFormError = combineFormError(saveErrors);

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErr(null);
    setSaveErrors([]);

    if (hasHistory === null) {
      setValidationErr("Please answer whether you've been arrested, detained, charged, or convicted.");
      return;
    }
    if (authorizeSSA === null || updateSSA === null) {
      setValidationErr("Please answer the SSA disclosure questions.");
      return;
    }
    const unanswered = EVER_QUESTIONS.find((q) => everAnswers[q.key] == null);
    if (unanswered) {
      setValidationErr(`Please answer: "${unanswered.label}"`);
      return;
    }

    const upserts: Promise<unknown>[] = [];
    const upsertStr = (key: string, val: string) => {
      if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    const upsertBool = (key: string, val: boolean | null) => {
      if (val !== null) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    upsertBool(FR_KEYS.authorizeSSA, authorizeSSA);
    upsertBool(FR_KEYS.updateSSA, updateSSA);
    upsertStr(FR_KEYS.selectiveServiceDate, selectiveServiceDate);
    upsertStr(FR_KEYS.selectiveServiceNumber, selectiveServiceNumber);
    upsertStr(FR_KEYS.uscisFieldOffice, uscisFieldOffice);
    upsertStr(FR_KEYS.nobilityTitles, nobilityTitles);
    for (const q of EVER_QUESTIONS) {
      upsertBool(FR_KEYS[q.key], everAnswers[q.key] ?? null);
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    navigate(`/cases/${id}/wizard/oath`);
  };

  if (applicantLoading || list.isLoading || fr.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Background</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS asks about every interaction you&apos;ve had with law enforcement,
          even if charges were dropped or expunged.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5">
        <legend className="font-medium flex items-baseline gap-1 flex-wrap">
          Have you ever been arrested, detained, charged, or convicted of any
          offense — anywhere in the world?
          <FieldHelpButton question="What counts as an arrest for the N-400? Does an expunged or dismissed charge need to be disclosed?" fieldKey="has_criminal_history" />
        </legend>
        <p className="text-xs text-slate-500 mt-1">
          Include traffic citations, expunged offenses, and military discipline.
        </p>
        <div className="flex gap-3 mt-4">
          {[
            { v: true, label: "Yes" },
            { v: false, label: "No" },
          ].map(({ v, label }) => (
            <button
              key={label}
              type="button"
              onClick={() => fr.upsert.mutate({ fieldKey: FR_KEY, value: v })}
              className={
                "px-5 py-2 rounded-lg border text-sm font-medium " +
                (hasHistory === v
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-400")
              }
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      {hasHistory === true && (
        <>
          <h2 className="font-semibold mt-4">Events</h2>
          <ul className="space-y-3">
            {list.items.map((e) => (
              <li
                key={e.id}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
              >
                <div className="text-sm">
                  <div className="font-medium capitalize">
                    {e.event_type.replace(/_/g, " ")}
                  </div>
                  <div className="text-slate-500">{e.offense_description}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {e.event_date} · {e.location_city}
                    {e.location_country ? `, ${e.location_country}` : ""}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setEditing(e)}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm("Delete this event?")) list.remove.mutate(e.id);
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {!editing && (
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              + Add event
            </button>
          )}

          {editing && (
            <CriminalForm
              initial={editing === "new" ? null : editing}
              onCancel={() => setEditing(null)}
              onSubmit={async (req) => {
                if (editing === "new") await list.create.mutateAsync(req);
                else await list.update.mutateAsync({ id: editing.id, req });
                setEditing(null);
              }}
              saving={list.create.isPending || list.update.isPending}
              error={editing === "new" ? list.create.error : list.update.error}
            />
          )}
        </>
      )}

      <form onSubmit={handleSaveAndContinue} className="space-y-6">

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <legend className="font-medium text-slate-900 px-1">
          Additional questions
        </legend>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-1">
            Authorize the SSA to release information from your records to USCIS?
          </p>
          <p className="text-xs text-slate-500 mb-2">
            Standard consent — defaults to Yes.
          </p>
          <YesNo value={authorizeSSA} onChange={setAuthorizeSSA} />
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-1">
            Update your Social Security Administration record after naturalisation?
          </p>
          <p className="text-xs text-slate-500 mb-2">
            USCIS forwards your updated status to the SSA so your record
            reflects U.S. citizenship.
          </p>
          <YesNo value={updateSSA} onChange={setUpdateSSA} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label={<>Selective Service registration date <FieldHelpButton question="What is Selective Service and how do I find my registration number?" fieldKey="selective_service_date" /></>}
            hint="Required only for males born after 1959 who lived in the U.S. between ages 18-26. Leave blank if not applicable."
            error={frFieldErrors?.[FR_KEYS.selectiveServiceDate]?.message}
          >
            <TextInput
              type="date"
              value={selectiveServiceDate}
              onChange={(e) => setSelectiveServiceDate(e.target.value)}
            />
          </Field>
          <Field
            label="Selective Service number"
            hint="11 digits from your Selective Service registration card."
            error={frFieldErrors?.[FR_KEYS.selectiveServiceNumber]?.message}
          >
            <TextInput
              value={selectiveServiceNumber}
              onChange={(e) => setSelectiveServiceNumber(e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Preferred USCIS field office for your interview"
          hint="City + state, e.g. 'Boston, MA'. Defaults to your local office if blank."
          error={frFieldErrors?.[FR_KEYS.uscisFieldOffice]?.message}
        >
          <TextInput
            value={uscisFieldOffice}
            onChange={(e) => setUscisFieldOffice(e.target.value)}
          />
        </Field>

        <Field
          label="Hereditary titles or orders of nobility"
          hint="List any inherited titles you currently hold or have held abroad. Leave blank if none — vanishingly rare."
          error={frFieldErrors?.[FR_KEYS.nobilityTitles]?.message}
        >
          <TextInput
            value={nobilityTitles}
            onChange={(e) => setNobilityTitles(e.target.value)}
            placeholder="e.g. Baron of Smallville (paternal)"
          />
        </Field>
      </fieldset>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <legend className="font-medium text-slate-900 px-1">
          Required disclosures
        </legend>
        <p className="text-sm text-slate-500">
          USCIS requires explicit answers to each of these — even when
          the answer is "No". Skip none.
        </p>
        {EVER_QUESTIONS.map((q) => (
          <div key={q.key}>
            <p className="text-sm font-medium text-slate-700 mb-1">{q.label}</p>
            {q.helper && (
              <p className="text-xs text-slate-500 mb-2">{q.helper}</p>
            )}
            <YesNo
              value={everAnswers[q.key] ?? null}
              onChange={(v) => setEverAnswers((s) => ({ ...s, [q.key]: v }))}
            />
          </div>
        ))}
      </fieldset>

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {frFieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link to={`/cases/${id}/wizard/children`} className="text-sm text-slate-600 hover:text-slate-900">
          ← Children
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
      </form>
    </div>
  );
}

function CriminalForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: CriminalEventResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateCriminalEventRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [form, setForm] = useState<CreateCriminalEventRequest>({
    // event_type values must match the DB CHECK constraint at
    // services/api/migrations/0004_cases_persons.sql:298 — earlier
    // versions of this form used "arrested"/"convicted"/etc. which
    // the backend silently rejected with 422.
    event_type: initial?.event_type ?? "arrest",
    event_date: initial?.event_date ?? "",
    location_city: initial?.location_city ?? "",
    location_country: initial?.location_country ?? "United States",
    offense_description: initial?.offense_description ?? "",
    disposition: initial?.disposition ?? "",
    sentence_details: initial?.sentence_details ?? "",
    was_in_custody: initial?.was_in_custody ?? null,
  });

  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const clean: CreateCriminalEventRequest = { ...form };
        (Object.keys(clean) as (keyof CreateCriminalEventRequest)[]).forEach((k) => {
          if (clean[k] === "") (clean as Record<string, unknown>)[k] = undefined;
        });
        void onSubmit(clean);
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit event" : "Add event"}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Event type"
          required
          error={fieldErrors?.["event_type"]?.message}
        >
          <Select
            value={form.event_type}
            onChange={(e) => setForm((s) => ({ ...s, event_type: e.target.value }))}
          >
            {/* Values must match the DB CHECK constraint exactly. */}
            <option value="arrest">Arrest</option>
            <option value="citation">Citation / ticket</option>
            <option value="charge">Charged</option>
            <option value="conviction">Conviction</option>
            <option value="probation">Probation</option>
            <option value="detention">Detention</option>
            <option value="other">Other (alternative sentencing, suspended sentence, parole, …)</option>
          </Select>
        </Field>
        <Field
          label="Date"
          required
          error={fieldErrors?.["event_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.event_date ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, event_date: e.target.value }))}
          />
        </Field>
        <Field label="City" error={fieldErrors?.["location_city"]?.message}>
          <TextInput
            value={form.location_city ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, location_city: e.target.value }))}
          />
        </Field>
        <Field
          label="Country"
          error={fieldErrors?.["location_country"]?.message}
        >
          <TextInput
            value={form.location_country ?? ""}
            onChange={(e) => setForm((s) => ({ ...s, location_country: e.target.value }))}
          />
        </Field>
      </div>

      <Field
        label="Offense description"
        required
        error={fieldErrors?.["offense_description"]?.message}
      >
        <TextInput
          value={form.offense_description ?? ""}
          onChange={(e) =>
            setForm((s) => ({ ...s, offense_description: e.target.value }))
          }
        />
      </Field>

      <Field
        label="Disposition"
        hint="Final outcome — dismissed, fine paid, sentenced, etc."
        error={fieldErrors?.["disposition"]?.message}
      >
        <TextInput
          value={form.disposition ?? ""}
          onChange={(e) => setForm((s) => ({ ...s, disposition: e.target.value }))}
        />
      </Field>

      <Field
        label="Sentence details"
        hint="Jail time, probation, fines"
        error={fieldErrors?.["sentence_details"]?.message}
      >
        <TextInput
          value={form.sentence_details ?? ""}
          onChange={(e) => setForm((s) => ({ ...s, sentence_details: e.target.value }))}
        />
      </Field>

      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add"}
        </button>
      </div>
    </form>
  );
}
